// ============================================================
// afterHitDamage - the damage the roll adds after the hits land
// ============================================================
// Toxic's failed Toughness test (1d10, ignoring armour and Toughness) and
// Corrosive's acid past the armour are not hits: the roll adds them after
// the hits, through the weapon-effect registry (weaponEffects.ts,
// hooks/applyHits/effects.ts). The forecasts used to stop at the hits, so
// with those specials the table dealt more than the plot showed - found by
// ./dev fuzz; the user's ruling (2026-09-28): the odds include it.
//
// So both forecasts run the SAME registry on each simulated attack and
// add its extra damage - the rule lives in one place, and a future effect
// that deals damage is forecast without a line here. The context mirrors
// the roll's, with two simplifications: the table's stacking policy is the
// default (it only shapes conditions, never this number), and a target
// counts as killed when a hit's critical carries K - the Protagonist's
// death floor, which can undo that, is not modelled.
//
// Pure apart from the dice channel the effects roll on, as every forecast is.

import { processAllWeaponEffects, type WeaponEffectContext } from './weaponEffects';
import { DEFAULT_STACK_POLICY } from './stackPolicy';
import type { HitResult } from './damageCalc';
import type { Slot, Target } from '../types';

/** A hit whose critical kills outright. */
const kills = (h: HitResult) => (h.crit?.fx ?? '').split(',').some((code) => code.trim() === 'K');

/** The extra damage the after-hit effects of `slot` add to these landed hits on `target`. */
export function afterHitDamage(target: Target, hits: readonly HitResult[], dos: number, slot: Slot): number {
  if (hits.length === 0) return 0;
  const ctx: WeaponEffectContext = {
    target,
    hits: [...hits],
    dos,
    specialRules: slot.specialRules ?? [],
    isMachine: (target.machineAP ?? 0) > 0,
    stacking: DEFAULT_STACK_POLICY,
    newConds: [...(target.conditions ?? [])],
    newArmour: { ...(target.armour ?? {}) },
    slot: { ...slot },
    dealtDamage: hits.some((h) => !h.ffBlocked && h.effectiveDamage > 0),
    targetKilled: hits.some(kills),
  };
  return processAllWeaponEffects(ctx).extraDamage;
}
